import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { Button, MenuItem, TextField, Typography } from '@/ui';

import { AssistantAppointmentCard } from './AssistantAppointmentCard';
import { AssistantBookSection } from './AssistantBookSection';
import { AssistantOperationNotice } from './AssistantOperationNotice';
import {
  AssistantPageShell,
  AssistantSignOutButton,
} from './AssistantPageShell';
import {
  assistantErrorStatus,
  schedulingAssistantApi,
  useGetAssistantAppointmentsQuery,
  useGetAssistedMembersQuery,
} from './api';
import {
  hasUnsettledAssistantOperation,
  useAssistantOperations,
} from './operationRecords';
import { assistantActionPath, assistantMemberPath } from './paths';
import { readAssistantSession, useAssistantSessionToken } from './session';
import {
  ASSISTANT_HOME_PATH,
  useEndAssistantSessionOnUnauthorized,
  useSignOutAssistant,
} from './useAssistantSession';

import type { AssistantAppointment, AssistedMember } from './api';
import type { FC } from 'react';

import { useAppDispatch } from '@/store/hooks';

const AppointmentActions: FC<{
  appointment: AssistantAppointment;
  /** A command for it has no answer yet: no second one until it does. */
  busy: boolean;
  memberId: string;
}> = ({ appointment, busy, memberId }) => {
  const navigate = useNavigate();
  if (busy) {
    return (
      <Typography color="text.secondary" variant="body2">
        Change in progress…
      </Typography>
    );
  }
  const { canCancelOccurrence, canRescheduleOccurrence } =
    appointment.capabilities;
  if (!canCancelOccurrence && !canRescheduleOccurrence) return null;
  return (
    <>
      {canRescheduleOccurrence ? (
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            navigate(
              assistantActionPath(memberId, appointment.id, 'reschedule'),
            )
          }
        >
          Reschedule
        </Button>
      ) : null}
      {canCancelOccurrence ? (
        <Button
          color="error"
          size="small"
          variant="text"
          onClick={() =>
            navigate(assistantActionPath(memberId, appointment.id, 'cancel'))
          }
        >
          Cancel
        </Button>
      ) : null}
    </>
  );
};

const MemberSwitcher: FC<{
  memberId: string;
  members: AssistedMember[];
}> = ({ memberId, members }) => {
  const navigate = useNavigate();
  return (
    <TextField
      label="Assisting"
      size="small"
      value={memberId}
      select
      onChange={(event) => navigate(assistantMemberPath(event.target.value))}
    >
      {members.map((member) => (
        <MenuItem key={member.memberId} value={member.memberId}>
          {member.displayName}
        </MenuItem>
      ))}
    </TextField>
  );
};

const MemberSessions: FC<{ memberId: string }> = ({ memberId }) => {
  const token = useAssistantSessionToken();
  // Skipped once the session ends, so a cache reset never refetches them.
  const members = useGetAssistedMembersQuery(token ? undefined : skipToken);
  const appointments = useGetAssistantAppointmentsQuery(
    token ? memberId : skipToken,
  );
  const { refetch: refetchAppointments } = appointments;
  const records = useAssistantOperations(memberId);
  const { isSigningOut, signOut } = useSignOutAssistant();
  useEndAssistantSessionOnUnauthorized(members.error, appointments.error);
  const dispatch = useAppDispatch();
  const forbidden = assistantErrorStatus(appointments.error) === 403;

  // The member removed this assistant: the cached list of whom they assist is
  // stale, and going back to it must not land here again.
  useEffect(() => {
    if (forbidden) {
      dispatch(schedulingAssistantApi.util.invalidateTags(['AssistedMembers']));
    }
  }, [dispatch, forbidden]);

  const member = members.currentData?.find(
    (candidate) => candidate.memberId === memberId,
  );
  const name = member?.displayName ?? 'this person';
  const otherMembers = (members.currentData?.length ?? 0) > 1;

  const headerActions = (
    <>
      {otherMembers && member && members.currentData ? (
        <MemberSwitcher memberId={memberId} members={members.currentData} />
      ) : null}
      <AssistantSignOutButton disabled={isSigningOut} onSignOut={signOut} />
    </>
  );

  let body;
  if (forbidden) {
    body = (
      <div className="flex flex-col gap-3" role="alert">
        <Typography variant="h4">
          You can&apos;t manage these sessions
        </Typography>
        <Typography variant="body1">
          You&apos;re not a scheduling assistant for this person anymore.
        </Typography>
        <div>
          <Link className="underline" to={ASSISTANT_HOME_PATH}>
            Back to the people you assist
          </Link>
        </div>
      </div>
    );
  } else if (appointments.isError) {
    body = (
      <div className="flex flex-col gap-3">
        <Typography variant="body1">
          We couldn&apos;t load the sessions.
        </Typography>
        <div>
          <Button variant="outlined" onClick={refetchAppointments}>
            Retry
          </Button>
        </div>
      </div>
    );
  } else if (!appointments.currentData) {
    body = <Typography variant="body1">Loading sessions…</Typography>;
  } else if (appointments.currentData.length === 0) {
    body = (
      <div className="flex flex-col gap-3">
        <Typography variant="h4">Upcoming sessions for {name}</Typography>
        <Typography variant="body1">
          There are no upcoming sessions to manage here. For a session that
          isn&apos;t listed, email concierge@mento.co and we&apos;ll help.
        </Typography>
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-4">
        <Typography variant="h4">Upcoming sessions for {name}</Typography>
        <ul className="flex flex-col gap-3">
          {appointments.currentData.map((appointment) => (
            <li key={appointment.id}>
              <AssistantAppointmentCard
                actions={
                  <AppointmentActions
                    appointment={appointment}
                    busy={hasUnsettledAssistantOperation(
                      records,
                      appointment.id,
                    )}
                    memberId={memberId}
                  />
                }
                appointment={appointment}
              />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <AssistantPageShell headerActions={headerActions}>
      <div className="flex flex-col gap-4">
        {records.length > 0 ? (
          // Mounting a notice finishes its command: send, replay or poll.
          <div aria-label="Your changes" className="flex flex-col gap-2">
            {records.map((record) => (
              <AssistantOperationNotice
                key={record.idempotencyKey}
                record={record}
              />
            ))}
          </div>
        ) : null}
        {forbidden ? null : (
          <AssistantBookSection
            memberId={memberId}
            memberName={name}
            records={records}
          />
        )}
        {body}
      </div>
    </AssistantPageShell>
  );
};

/** `/assistant/members/:memberId`: what to book for one member, and their
 * upcoming sessions. */
const AssistantMemberSessions: FC = () => {
  const { memberId } = useParams();
  // Checked on arrival only: once a session ends here, useEndAssistantSession
  // does the navigating, with the reason.
  const arrivedWithSession = useMemo(() => readAssistantSession() !== null, []);

  if (!memberId || !arrivedWithSession) {
    return <Navigate to={ASSISTANT_HOME_PATH} replace />;
  }
  // Keyed so switching member starts from a clean page.
  return <MemberSessions key={memberId} memberId={memberId} />;
};

export default AssistantMemberSessions;
